import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import FileUploadInput, { FILE_ACCEPT_TYPES } from '../../components/admin/FileUploadInput';
import { Building2, Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AboutManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [fileData, setFileData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/about`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadSingleFile = async (file, subfolder) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append('file', file);
    // Use query parameter for subfolder
    const res = await axios.post(`${API_URL}/api/files/upload?subfolder=${subfolder}`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    return `/api/files/${res.data.id}`;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      let finalCampusUrl = data.campus_image_url;
      if (fileData.campus_image) {
        finalCampusUrl = await uploadSingleFile(fileData.campus_image, 'about');
      }

      const payload = { ...data, campus_image_url: finalCampusUrl };

      await axios.post(`${API_URL}/api/about`, payload, {
        headers: getAuthHeaders()
      });

      setFileData({});
      alert('About Us page updated successfully!');
      fetchData(); // Refresh to ensure everything is consistent
    } catch (error) {
      console.error('Error saving:', error);
      setError(error.response?.data?.detail || 'Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/about/upload-campus-image`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setData({ ...data, campus_image_url: response.data.file_url });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const addHistoryMilestone = () => {
    setData({
      ...data,
      history_milestones: [
        ...data.history_milestones,
        { year: '', description: '' }
      ]
    });
  };

  const updateHistoryMilestone = (index, field, value) => {
    const updated = [...data.history_milestones];
    updated[index][field] = value;
    setData({ ...data, history_milestones: updated });
  };

  const deleteHistoryMilestone = (index) => {
    setData({
      ...data,
      history_milestones: data.history_milestones.filter((_, i) => i !== index)
    });
  };

  const addAchievement = () => {
    setData({
      ...data,
      achievements: [
        ...data.achievements,
        { label: '', value: '', icon: 'Award' }
      ]
    });
  };

  const updateAchievement = (index, field, value) => {
    const updated = [...data.achievements];
    updated[index][field] = value;
    setData({ ...data, achievements: updated });
  };

  const deleteAchievement = (index) => {
    setData({
      ...data,
      achievements: data.achievements.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg text-red-600">Failed to load data</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-8 h-8 text-[#0E6D2B]" />
              About Us Management
            </h1>
            <p className="text-gray-600 mt-1">Manage About ICAR-CISH page content</p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Page Header</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Page Title</label>
              <Input
                value={data.page_title}
                onChange={(e) => setData({ ...data, page_title: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Page Subtitle</label>
              <Input
                value={data.page_subtitle}
                onChange={(e) => setData({ ...data, page_subtitle: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Main Content</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">First Paragraph</label>
              <Textarea
                value={data.main_paragraph_1}
                onChange={(e) => setData({ ...data, main_paragraph_1: e.target.value })}
                rows={4}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Second Paragraph</label>
              <Textarea
                value={data.main_paragraph_2}
                onChange={(e) => setData({ ...data, main_paragraph_2: e.target.value })}
                rows={4}
              />
            </div>
          </div>
        </div>

        {/* Campus Image */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Campus Image</h2>
          <div className="space-y-4">
            <div>
              <FileUploadInput
                field={{ name: 'campus_image', type: 'file', fileType: 'image' }}
                value={fileData.campus_image}
                existingUrl={data.campus_image_url}
                formData={data}
                setFormData={setData}
                onChange={(files) => setFileData({ ...fileData, campus_image: files })}
                onDeleteServerFile={() => { setData({ ...data, campus_image_url: '' }); return true; }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Image Caption</label>
              <Input
                value={data.campus_image_caption}
                onChange={(e) => setData({ ...data, campus_image_caption: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Vision & Mission</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Vision Text</label>
              <Textarea
                value={data.vision_text}
                onChange={(e) => setData({ ...data, vision_text: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Mission Text</label>
              <Textarea
                value={data.mission_text}
                onChange={(e) => setData({ ...data, mission_text: e.target.value })}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* History Milestones */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">History Milestones</h2>
            <Button onClick={addHistoryMilestone} size="sm" variant="outline">
              <Plus className="w-4 h-4 mr-1" />
              Add Milestone
            </Button>
          </div>
          <div className="space-y-3">
            {data.history_milestones && data.history_milestones.map((milestone, idx) => (
              <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-700">Milestone {idx + 1}</h3>
                  <button
                    onClick={() => deleteHistoryMilestone(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Year</label>
                    <Input
                      value={milestone.year}
                      onChange={(e) => updateHistoryMilestone(idx, 'year', e.target.value)}
                      placeholder="1972"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <Input
                      value={milestone.description}
                      onChange={(e) => updateHistoryMilestone(idx, 'description', e.target.value)}
                      placeholder="Event description"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Achievements */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Key Achievements</h2>
            <Button onClick={addAchievement} size="sm" variant="outline">
              <Plus className="w-4 h-4 mr-1" />
              Add Achievement
            </Button>
          </div>
          <div className="space-y-3">
            {data.achievements && data.achievements.map((achievement, idx) => (
              <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-700">Achievement {idx + 1}</h3>
                  <button
                    onClick={() => deleteAchievement(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Label</label>
                    <Input
                      value={achievement.label}
                      onChange={(e) => updateAchievement(idx, 'label', e.target.value)}
                      placeholder="Scientists"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Value</label>
                    <Input
                      value={achievement.value}
                      onChange={(e) => updateAchievement(idx, 'value', e.target.value)}
                      placeholder="50+"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Icon</label>
                    <select
                      value={achievement.icon}
                      onChange={(e) => updateAchievement(idx, 'icon', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="Award">Award</option>
                      <option value="Users">Users</option>
                      <option value="Building2">Building2</option>
                      <option value="History">History</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
